import { ICurrentUser } from '@dadohr/shared/domainTypes/currentUser';
import { toDoc } from '@dadohr/shared/domainTypes/document';
import { isFeatureEnabled } from '@dadohr/shared/domainTypes/features';
import { PATHS } from '@dadohr/shared/domainTypes/routes';
import { TestAccount } from '@dadohr/shared/domainTypes/testAccount';
import { FS } from '@dadohr/shared/versions';
import DateFnsUtils from '@date-io/moment';
import { ErrorBoundary } from 'react-error-boundary';
// choose your lib
import { AdminAppErrorData } from '@dadohr/shared/domainTypes/internalAlerts';
import styled from '@emotion/styled';
import { CssBaseline } from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import firebase from 'firebase/app';
import * as qS from 'query-string';
import React, { useEffect, useMemo, useRef } from 'react';
import { useCollection } from 'react-firebase-hooks/firestore';
import ReactHelmet from 'react-helmet';
import { Redirect, Route, BrowserRouter as Router } from 'react-router-dom';
import { GoogleAnalyticsListener } from './components/GoogleAnalyticsLIstener';
import { Loader } from './components/Loader';
import { ScrollToTopOnRouteChange } from './components/ScrollToTopOnRouteChange';
import { SomethingWentWrong } from './components/SomethingWentWrong';
import { PageAcceptInvitation } from './features/AdminUsers/pages/AcceptInvitation';
import {
  ExchangeTokens,
  Login,
  LoginWithSsoCheck,
  SsoLoginTest
} from './features/Login';
import { PageMaintenanceNewRelease } from './features/Maintenance/pages/NewRelease';
import { ConfirmationOverview } from './features/SignUp/Pages/Confirmation';
import { SignUpOverview } from './features/SignUp/Pages/Overview';
import { BackgroundJobsProvider } from './hooks/useBackgroundJobs';
import { useFullstoryIdentify } from './hooks/useFullstoryIdentify';
import { useHelpscoutIdentify } from './hooks/useHelpscoutIdentify';
import { HotkeysProvider } from './hooks/useHotkeys';
import { usePromise } from './hooks/usePromise';
import { useRouter } from './hooks/useRouter';
import { SideNavProvider } from './hooks/useSideNav';
import { SnackbarProviderConfigured } from './hooks/useSnackbar';
import { useTranslation } from './i18next';
import { Centered } from './layout/Centered';
import { TopNav, TopNavBarLogoOnly } from './layout/TopNav';
import './polyfills';
import { useRoutes, useStringQueryParam } from './routes';
import { captureException } from './sentry';
import { checkSession, signOut, useLogin } from './services/auth';
import { decodeObjBase64 } from './services/base64';
import { useCurrentUser } from './services/currentUser';
import { getStringFromUnkownError } from './services/errors';
import { auth, store, useMappedLoadingValue } from './services/firebase';
import { IntegrationsContextProvider } from './services/integrations/context';
import { tryReportAdminAppError } from './services/internalAlerts';
import { isFreeTierOrg } from './services/organizationTier';
import { ONE_MINUTE } from './services/time';
import { DadoCopilotGated } from './components/DadoCopilot/DadoCopilotGated';
import { FeatureAnnouncementModal } from './components/FeatureAnnouncementModal';
import { CopilotProvider } from './components/DadoCopilot/CopilotContext';
import { COPILOT_PUSH_TRANSITION } from './components/DadoCopilot/useCopilotPageBehavior';
import { ExperiencesPreviewPage, IPage, PAGES } from './sitemap';
import { AppThemeProvider } from './themes/provider';

const Root = styled('div')`
  display: flex;
  min-height: 100vh;
`;

const AppContent = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: ${(p) => p.theme.custom.colors.background.main};
`;

// Wraps the routed content so the copilot sidebar can push it left
// (HubSpot-style) on /experiences/:id. The push width is exposed as
// --copilot-push by useCopilotPageBehavior; 0px elsewhere. TopNav stays
// outside this wrapper so it isn't pushed.
const RoutesPushable = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding-right: var(--copilot-push, 0px);
  transition: ${COPILOT_PUSH_TRANSITION};
`;

const AppPage = ({ page }: { page: IPage }) => {
  const { t } = useTranslation();
  const title = useMemo(() => `Dado | ${page.titleFragment(t)}`, [page, t]);
  const currentUser = useCurrentUser();
  return (
    <>
      {page.redirectFrom &&
        page.redirectFrom.map((p) => (
          <Route
            path={p}
            key={p}
            exact
            render={() => <Redirect to={page.path} />}
          />
        ))}
      <Route
        key={page.path}
        path={page.path}
        exact
        render={(props) => (
          <>
            <ReactHelmet>
              <title>{title}</title>
            </ReactHelmet>
            <ErrorBoundary
              fallbackRender={() => <SomethingWentWrong height={500} />}
              onError={async (err: unknown, info: any) => {
                captureException(err, {
                  tags: { errorBoundary: 'app' },
                  extra: { componentStack: info?.componentStack }
                });
                const url = window?.location?.href || '';
                const data: AdminAppErrorData = {
                  url,
                  error: getStringFromUnkownError(err),
                  info: getStringFromUnkownError(info),
                  userAgent: window.navigator.userAgent,
                  currentUser: currentUser
                };
                await tryReportAdminAppError(data);
              }}
            >
              {page.render(props)}
            </ErrorBoundary>
          </>
        )}
      />
    </>
  );
};

const UserTestingAutoLogin = () => {
  const { goTo } = useRoutes();
  const [testAccount] = useMappedLoadingValue(
    useCollection(
      store()
        .collection(FS.testAccounts)
        .where('available', '==', true)
        .orderBy('email')
        .limit(1)
    ),
    (s) => {
      if (s.empty) {
        console.log('No test account available');
        return null;
      }
      return toDoc<TestAccount>(s.docs[0]);
    }
  );

  useEffect(() => {
    if (!testAccount) {
      return;
    }

    auth()
      .signInWithEmailAndPassword(
        testAccount.data.email,
        testAccount.data.password
      )
      .then(async () => {
        await store()
          .collection(FS.testAccounts)
          .doc(testAccount.id)
          .update({
            available: false,
            used: firebase.firestore.FieldValue.increment(1)
          });
        goTo('/');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testAccount]);

  return <Loader height={500} />;
};

const InviteOnly = () => {
  useEffect(() => {
    setTimeout(() => {
      signOut();
    }, 5000);
  }, []);

  return (
    <Centered height={300}>
      Invite only! You'll be logged out in 5 seconds.
    </Centered>
  );
};

const useFullstoryIdentifyForApp = (currentUser: ICurrentUser | null) => {
  const d = useMemo(
    () =>
      currentUser
        ? {
            user: currentUser,
            org: currentUser.org
          }
        : null,
    [currentUser]
  );
  useFullstoryIdentify('app', d);
};

const useHelpscoutIdentifyForApp = (currentUser: ICurrentUser | null) => {
  const d = useMemo(
    () =>
      currentUser
        ? {
            user: currentUser,
            org: currentUser.org
          }
        : null,
    [currentUser]
  );
  useHelpscoutIdentify(d);
};

const DEBUG_SESSION_VALIDATOR = false;
const SIGN_OUT_ON_INVALID_SESSION = false;

const SessionValidator: React.FC = ({ children }) => {
  const isVisible = useRef(true);
  const [status, loading, error, forceReload] = usePromise(() => {
    return checkSession();
  }, []);

  if (DEBUG_SESSION_VALIDATOR) {
    console.log(
      `isSession valid (loading: ${loading}): ${loading ? '' : status?.status}`
    );
  }

  useEffect(() => {
    const listener = () => {
      const visible = document.visibilityState !== 'hidden';
      isVisible.current = visible;
      if (visible) {
        if (DEBUG_SESSION_VALIDATOR) {
          console.log('re-check session on vis change');
        }
        forceReload();
      }
    };
    window.addEventListener('visibilitychange', listener);
    return () => window.removeEventListener('visibilitychange', listener);
  }, [forceReload]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isVisible.current) {
        if (DEBUG_SESSION_VALIDATOR) {
          console.log('re-check session on interval');
        }
        forceReload();
      }
    }, ONE_MINUTE * 5);
    return () => clearInterval(interval);
  }, [forceReload]);

  if (
    !loading &&
    (error || status?.status !== 'VALID') &&
    SIGN_OUT_ON_INVALID_SESSION
  ) {
    signOut();
    return null;
  }

  return <>{children}</>;
};

const MainApp = () => {
  const { ROUTES, getPath, goTo, getQuery } = useRoutes();
  const path = getPath();
  const [orgId] = useStringQueryParam('orgId');
  const { currentUser, status } = useLogin(orgId);

  useFullstoryIdentifyForApp(currentUser);
  useHelpscoutIdentifyForApp(currentUser);

  if (status === 'INIT') {
    return null;
  }

  if (path.startsWith('/sso/test')) {
    return (
      <Root>
        <CssBaseline>
          <SsoLoginTest />
        </CssBaseline>
      </Root>
    );
  }

  if (path.startsWith('/accept-invitation')) {
    return (
      <Root>
        <CssBaseline>
          <PageAcceptInvitation />
        </CssBaseline>
      </Root>
    );
  }

  if (path.startsWith('/authenticate')) {
    return (
      <Root>
        <CssBaseline>
          <ExchangeTokens />
        </CssBaseline>
      </Root>
    );
  }

  if (path.startsWith('/experiencesPreview/')) {
    return (
      <Root>
        <CssBaseline>
          <AppPage page={ExperiencesPreviewPage} />
        </CssBaseline>
      </Root>
    );
  }

  if (status === 'NO_LOGIN') {
    if (path === '/usertesting') {
      return (
        <Root>
          <CssBaseline>
            <UserTestingAutoLogin />;
          </CssBaseline>
        </Root>
      );
    }
    if (path === PATHS.signUp.register) {
      return (
        <Root>
          <CssBaseline>
            <SignUpOverview />
          </CssBaseline>
        </Root>
      );
    }
    if (path === PATHS.signUp.confirm) {
      return (
        <Root>
          <CssBaseline>
            <ConfirmationOverview />
          </CssBaseline>
        </Root>
      );
    }
    if (getQuery()['adminLogin'] || getQuery()['useGoogle']) {
      return (
        <Root>
          <CssBaseline>
            <Login />
          </CssBaseline>
        </Root>
      );
    }

    return (
      <Root>
        <CssBaseline>
          <LoginWithSsoCheck />
        </CssBaseline>
      </Root>
    );
  }

  if (status === 'NO_USER') {
    return <InviteOnly />;
  }

  if (status === 'NO_ORGS') {
    return <Centered height={300}>Not added to a company yet!</Centered>;
  }

  if (status !== 'SUCCESS' || !currentUser) {
    return <Centered height={300}>Something went wrong.</Centered>;
  }
  if (path === '/usertesting') {
    goTo('/');
  }

  if (
    isFeatureEnabled(
      currentUser.org.features,
      'MAINTENANCE_PAGE_NEW_RELEASE_V1'
    )
  ) {
    return (
      <Root>
        <CssBaseline>
          <AppContent>
            <TopNavBarLogoOnly />
            <PageMaintenanceNewRelease />
          </AppContent>
        </CssBaseline>
      </Root>
    );
  }

  if (
    isFreeTierOrg(currentUser.org) &&
    !currentUser.org.termsOfServiceAndPrivacyPolicy?.accepted &&
    path !== PATHS.signUp.updateOrgInfo
  ) {
    goTo(ROUTES.signUp.updateOrgInfo.url());
    return null;
  }

  return (
    <SessionValidator>
      <IntegrationsContextProvider>
        <CopilotProvider>
          <Root>
            <CssBaseline />

            <AppContent>
              {path === PATHS.signUp.updateOrgInfo ? (
                <TopNavBarLogoOnly />
              ) : (
                <TopNav />
              )}
              <RoutesPushable>
                <Route
                  path="/"
                  exact
                  render={() => (
                    <Redirect
                      to={
                        isFreeTierOrg(currentUser.org)
                          ? ROUTES.moments.overview.url()
                          : ROUTES.experiences.overview.url()
                      }
                    />
                  )}
                />
                {PAGES.map((page) => (
                  <AppPage key={page.path} page={page} />
                ))}
              </RoutesPushable>
            </AppContent>
            <DadoCopilotGated />
            <FeatureAnnouncementModal />
          </Root>
        </CopilotProvider>
      </IntegrationsContextProvider>
    </SessionValidator>
  );
};

// We need this to make sure that an orgId query param is present,
// which on an oauth redirect is buried inside the state param
const OAuthRedirectGuard: React.FC = ({ children }) => {
  const { location } = useRouter();
  if (location.pathname.indexOf('oauth') !== -1) {
    const params = qS.parse(location.search);
    if (typeof params.state === 'string' && !params.orgId) {
      const { orgId } = decodeObjBase64<{ orgId: string }>(params.state);
      params.orgId = orgId;
      return (
        <Redirect
          to={{
            ...location,
            search: qS.stringify(params)
          }}
        />
      );
    }
  }
  return <>{children}</>;
};

const App = () => {
  return (
    <HotkeysProvider>
      <SideNavProvider>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <AppThemeProvider>
            <SnackbarProviderConfigured>
              <BackgroundJobsProvider>
                <Router>
                  <GoogleAnalyticsListener app="app" />
                  <OAuthRedirectGuard>
                    <ScrollToTopOnRouteChange />
                    <MainApp />
                  </OAuthRedirectGuard>
                </Router>
              </BackgroundJobsProvider>
            </SnackbarProviderConfigured>
          </AppThemeProvider>
        </MuiPickersUtilsProvider>
      </SideNavProvider>
    </HotkeysProvider>
  );
};

export default App;
